import { makeAutoObservable, runInAction } from 'mobx'
import { adminGetOrgStudents } from 'api/admin'
import { mapBackendStudentToFrontend } from 'api/users'
import { organizationContextStore } from 'shared/stores/organizationContextStore'
import { counselorsStore } from './counselorsStore'
import { toUserManagementStudent } from './userManagementMapping'

class UserManagementStore {
	loadStatus = 'idle'
	error = null
	_inflight = null
	_students = []

	constructor() {
		makeAutoObservable(this, { _inflight: false }, { autoBind: true })
	}

	get students() {
		return this._students.map(toUserManagementStudent)
	}

	get counselors() {
		return counselorsStore.counselors
	}

	get isLoading() {
		return (
			this.loadStatus === 'loading' ||
			counselorsStore.loadStatus === 'loading' ||
			organizationContextStore.loadStatus === 'loading'
		)
	}

	async ensureLoaded({ force = false } = {}) {
		if (this._inflight) return this._inflight

		this.loadStatus = 'loading'
		this.error = null

		this._inflight = (async () => {
			try {
				await organizationContextStore.ensureInitialized({ force })
				if (!organizationContextStore.isReady) {
					runInAction(() => {
						this._students = []
						this.loadStatus = 'success'
					})
					return []
				}

				const [studentsPayload] = await Promise.all([
					adminGetOrgStudents().then(response => response.data),
					counselorsStore.ensureLoaded({ force }),
				])

				const backendStudents = studentsPayload?.students || []
				const mapped = backendStudents.map((student, index) =>
					mapBackendStudentToFrontend(student, index),
				)
				const seen = new Set()
				const deduped = mapped.filter(student => {
					const id = String(student?.id ?? '').trim()
					if (!id || seen.has(id)) return false
					seen.add(id)
					return true
				})

				runInAction(() => {
					this._students = deduped
					this.loadStatus = 'success'
				})
				return deduped
			} catch (error) {
				runInAction(() => {
					this.loadStatus = 'error'
					this.error =
						error?.response?.data?.error ||
						error?.message ||
						'Failed to load user management data'
				})
				throw error
			} finally {
				this._inflight = null
			}
		})()

		return this._inflight
	}

	resetForOrganizationChange() {
		this._students = []
		this.loadStatus = 'idle'
		this.error = null
		counselorsStore.loadStatus = 'idle'
	}
}

export const userManagementStore = new UserManagementStore()
